import type { TourSummary, TourUpsertRequest } from '../lib/api/tourOperators'
import { DEFAULT_HIKE_DIFFICULTY, parseHikeDifficulty, type HikeDifficulty, type HikeDifficultyMode } from '../lib/hikeDifficulty'
import type { MeasurementUnit } from '../lib/measurements'
import { DEFAULT_PLAN_GENERATION_PROFILE, toTourPlanGenerationProfile, type TourPlanGenerationProfile } from '../lib/planGenerationProfile'

export const MAX_ITINERARY_DAYS = 16

export type DayFormState = {
  dayNumber: string
  distanceKm: string
  elevationGainMeters: string
  elevationLossMeters: string
}

export type TourFormState = {
  sourceMeasurementUnit: MeasurementUnit
  name: string
  link: string
  description: string
  country: string
  stateOrProvince: string
  geographicRegion: string
  city: string
  protectedArea: string
  difficulty: string
  difficultyLevel: HikeDifficulty
  difficultyMode: HikeDifficultyMode
  planGenerationProfile: TourPlanGenerationProfile
  packWeight: string
  highestPoint: string
  aboveEightThousandFeet: '' | 'yes' | 'no'
  itinerary: DayFormState[]
}

const createDayFormState = (dayNumber: number, seed?: Partial<DayFormState>): DayFormState => ({
  dayNumber: seed?.dayNumber ?? dayNumber.toString(),
  distanceKm: seed?.distanceKm ?? '',
  elevationGainMeters: seed?.elevationGainMeters ?? '',
  elevationLossMeters: seed?.elevationLossMeters ?? ''
})

export const createEmptyTourForm = (sourceMeasurementUnit: MeasurementUnit = 'Metric'): TourFormState => ({
  sourceMeasurementUnit,
  name: '',
  link: '',
  description: '',
  country: '',
  stateOrProvince: '',
  geographicRegion: '',
  city: '',
  protectedArea: '',
  difficulty: '',
  difficultyLevel: DEFAULT_HIKE_DIFFICULTY,
  difficultyMode: 'Calculated',
  planGenerationProfile: DEFAULT_PLAN_GENERATION_PROFILE,
  packWeight: '',
  highestPoint: '',
  aboveEightThousandFeet: '',
  itinerary: [createDayFormState(1)]
})

export const mapTourToForm = (
  tour: TourSummary,
  fallbackMeasurementUnit: MeasurementUnit = 'Metric'
): TourFormState => {
  const itinerarySource = tour.itinerary.length > 0
    ? tour.itinerary
    : [{
        dayNumber: 1,
        distanceKm: null,
        elevationGainMeters: null,
        elevationLossMeters: null
      }]

  return {
    sourceMeasurementUnit: tour.sourceMeasurementUnit ?? fallbackMeasurementUnit,
    name: tour.name,
    link: tour.link ?? '',
    description: tour.description ?? '',
    country: tour.country ?? '',
    stateOrProvince: tour.stateOrProvince ?? '',
    geographicRegion: tour.geographicRegion ?? '',
    city: tour.city ?? '',
    protectedArea: tour.protectedArea ?? '',
    difficulty: tour.difficulty ?? '',
    difficultyLevel: parseHikeDifficulty(tour.difficultyLevel) ?? DEFAULT_HIKE_DIFFICULTY,
    difficultyMode: tour.difficultyMode ?? 'Legacy',
    planGenerationProfile: toTourPlanGenerationProfile(tour.planGenerationProfile),
    packWeight: tour.packWeight ?? '',
    highestPoint: tour.highestPoint ?? '',
    aboveEightThousandFeet: tour.aboveEightThousandFeet === true ? 'yes' : tour.aboveEightThousandFeet === false ? 'no' : '',
    itinerary: itinerarySource.map((day, index) =>
      createDayFormState(day.dayNumber ?? index + 1, {
        dayNumber: (day.dayNumber ?? index + 1).toString(),
        distanceKm: day.distanceKm != null ? day.distanceKm.toString() : '',
        elevationGainMeters: day.elevationGainMeters != null ? day.elevationGainMeters.toString() : '',
        elevationLossMeters: day.elevationLossMeters != null ? day.elevationLossMeters.toString() : ''
      })
    )
  }
}

const parseNumericInput = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const parseDayNumber = (value: string, fallback: number) => {
  const parsed = Number.parseInt(value, 10)
  if (Number.isFinite(parsed) && parsed >= 1 && parsed <= MAX_ITINERARY_DAYS) {
    return parsed
  }
  return fallback
}

const trimOrNull = (value: string) => {
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

const flagToBool = (value: TourFormState['aboveEightThousandFeet']) => {
  if (value === 'yes') return true
  if (value === 'no') return false
  return null
}

export const tourFormToPayload = (form: TourFormState): TourUpsertRequest => {
  const itinerary = form.itinerary
    .map((day, index) => ({
      dayNumber: parseDayNumber(day.dayNumber, index + 1),
      distanceKm: parseNumericInput(day.distanceKm),
      elevationGainMeters: parseNumericInput(day.elevationGainMeters),
      elevationLossMeters: parseNumericInput(day.elevationLossMeters),
      sourceMeasurementUnit: form.sourceMeasurementUnit
    }))
    .sort((a, b) => a.dayNumber - b.dayNumber)

  return {
    name: form.name.trim(),
    link: trimOrNull(form.link),
    description: trimOrNull(form.description),
    country: trimOrNull(form.country),
    stateOrProvince: trimOrNull(form.stateOrProvince),
    geographicRegion: trimOrNull(form.geographicRegion),
    city: trimOrNull(form.city),
    protectedArea: trimOrNull(form.protectedArea),
    difficulty: trimOrNull(form.difficulty),
    difficultyLevel: form.difficultyMode === 'Manual' ? form.difficultyLevel : null,
    difficultyMode: form.difficultyMode,
    planGenerationProfile: form.planGenerationProfile,
    sourceMeasurementUnit: form.sourceMeasurementUnit,
    packWeight: trimOrNull(form.packWeight),
    highestPoint: trimOrNull(form.highestPoint),
    aboveEightThousandFeet: flagToBool(form.aboveEightThousandFeet),
    itinerary
  }
}

export const addDay = (form: TourFormState): TourFormState => {
  if (form.itinerary.length >= MAX_ITINERARY_DAYS) {
    return form
  }
  return {
    ...form,
    itinerary: [...form.itinerary, createDayFormState(form.itinerary.length + 1)]
  }
}

export const removeDay = (form: TourFormState, index: number): TourFormState => {
  if (form.itinerary.length === 1) {
    return form
  }
  return {
    ...form,
    itinerary: form.itinerary.filter((_, idx) => idx !== index)
  }
}

export const updateDayField = (
  form: TourFormState,
  index: number,
  field: keyof DayFormState,
  value: string
): TourFormState => ({
  ...form,
  itinerary: form.itinerary.map((day, idx) => {
    if (idx !== index) {
      return day
    }
    return { ...day, [field]: value }
  })
})
